import React, { useState, useEffect, useRef } from 'react';
import { X, AlertCircle, CheckCircle2, LoaderCircle } from 'lucide-react';
import {
  checkGoogleCalendarConnection,
  disconnectGoogleCalendar
} from '../../service/apiService';
import GoogleCalendarIcon from '../../icons/googleCalendar';

// Declare global google object for TypeScript
declare global {
  interface Window {
    google: any;
  }
}

interface GoogleCalendarAuthProps {
  isOpen: boolean;
  onClose: () => void;
  onAuthSuccess: (code: string) => void;
}

const GoogleCalendarAuth: React.FC<GoogleCalendarAuthProps> = ({
  isOpen,
  onClose,
  onAuthSuccess,
}) => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [authSuccess, setAuthSuccess] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [checkingConnection, setCheckingConnection] = useState(false);
  const [lastAction, setLastAction] = useState<'connect' | 'disconnect' | null>(null);
  const codeClientRef = useRef<any>(null);

  // Google Calendar OAuth configuration
  const GOOGLE_CALENDAR_CLIENT_ID = process.env.REACT_APP_GOOGLE_CALENDAR_CLIENT_ID || ""
  const GOOGLE_CALENDAR_SCOPE = 'https://www.googleapis.com/auth/calendar';

  useEffect(() => {
    if (!isOpen) {
      setAuthError(null);
      setAuthSuccess(false);
      setIsConnecting(false);
      setIsDisconnecting(false);
      setLastAction(null);
    } else {
      // Check connection status when modal opens
      checkConnectionStatus();
    }
  }, [isOpen]);

  const checkConnectionStatus = async () => {
    console.log('Checking connection status...');
    setCheckingConnection(true);
    try {
      const connected = await checkGoogleCalendarConnection();
      console.log('Connection status:', connected);
      setIsConnected(connected);
    } catch (error) {
      console.error('Error checking Google Calendar connection:', error);
    } finally {
      setCheckingConnection(false);
    }
  };

  useEffect(() => {
    // Initialize Google Identity Services client when component mounts.
    // Skip when no client id is configured: initCodeClient throws
    // "Missing required parameter client_id" on an empty value, and this
    // effect runs on page load even while the modal is closed.
    if (window.google && GOOGLE_CALENDAR_CLIENT_ID && !codeClientRef.current) {
      codeClientRef.current = window.google.accounts.oauth2.initCodeClient({
        client_id: GOOGLE_CALENDAR_CLIENT_ID,
        scope: GOOGLE_CALENDAR_SCOPE,
        ux_mode: 'popup',
        access_type: 'offline',
        prompt: 'consent',
        callback: (response: any) => {
          console.log('Full OAuth response:', response);

          if (response.code) {
            setIsConnecting(false);
            setAuthSuccess(true);
            setLastAction('connect');
            onAuthSuccess(response.code);

            // Update connection status
            setTimeout(() => {
              checkConnectionStatus();
              setAuthSuccess(false);
            }, 1500);
          } else {
            setIsConnecting(false);
            setAuthError('Authentication failed');
          }
        }
      });
    }
  }, [GOOGLE_CALENDAR_CLIENT_ID, GOOGLE_CALENDAR_SCOPE, onAuthSuccess]);

  const handleConnect = () => {
    console.log('Connect button clicked');
    setLastAction('connect');

    if (!window.google) {
      setAuthError('Google Identity Services not loaded');
      return;
    }

    if (!GOOGLE_CALENDAR_CLIENT_ID) {
      setAuthError('Google Calendar Client ID not configured');
      return;
    }

    setIsConnecting(true);
    setAuthError(null);

    try {
      codeClientRef.current?.requestCode();
    } catch (error) {
      setIsConnecting(false);
      setAuthError('Failed to initiate Google Calendar authentication');
      console.error('Google Calendar auth error:', error);
    }
  };

  const handleDisconnect = async () => {
    console.log('Disconnect button clicked');
    setLastAction('disconnect');

    setIsDisconnecting(true);
    setAuthError(null);

    try {
      console.log('Calling disconnectGoogleCalendar API...');
      const result = await disconnectGoogleCalendar();
      console.log('API response:', result);

      if (result.success) {
        console.log('Disconnect successful!');
        setIsConnected(false);
        setAuthSuccess(true);

        setTimeout(() => {
          console.log('Closing modal after successful disconnect');
          onClose();
        }, 1500);
      } else {
        console.log('Disconnect failed with error:', result.error);
        setAuthError(result.error || 'Failed to disconnect Google Calendar account');
      }
    } catch (error) {
      console.error('Exception in disconnect:', error);
      setAuthError('Failed to disconnect Google Calendar account. Please try again.');
    } finally {
      console.log('Disconnect process completed');
      setIsDisconnecting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 scrim flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-base font-semibold text-gray-900 flex items-center">
            <GoogleCalendarIcon size={18} className="mr-2" />
            Google Calendar Connection
          </h3>
          <button
  onClick={() => {
    setIsConnecting(false);
    setIsDisconnecting(false);
    onClose();
  }}
  className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded hover:bg-gray-100"
>
  <X size={20} />
</button>
        </div>

        {checkingConnection ? (
          <div className="text-center py-8">
            <LoaderCircle className="w-8 h-8 text-violet-600 animate-spin mx-auto mb-3" />
            <p className="text-sm text-gray-600">Verifying connection...</p>
          </div>
        ) : authSuccess ? (
          <div className="text-center py-8">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mb-3">
              <CheckCircle2 className="text-green-600" size={24} />
            </div>
            <h4 className="text-base font-semibold text-gray-900 mb-1">
              {lastAction === 'connect' ? 'Connected Successfully' : 'Disconnected Successfully'}
            </h4>
            <p className="text-sm text-gray-600">
              {lastAction === 'connect'
                ? 'Your Google Calendar account is now connected.'
                : 'Your Google Calendar account has been removed.'
              }
            </p>
          </div>
        ) : (
          <>
            {isConnected && (
              <div className="bg-green-50 border border-green-200 rounded-md p-3 mb-4">
                <div className="flex items-center">
                  <CheckCircle2 className="text-green-600 mr-2 flex-shrink-0" size={16} />
                  <span className="text-sm text-green-800">Account connected</span>
                </div>
              </div>
            )}

            {authError && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                <div className="flex items-start">
                  <AlertCircle className="text-red-500 mr-2 flex-shrink-0 mt-0.5" size={16} />
                  <p className="text-sm text-red-700">{authError}</p>
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={handleConnect}
                disabled={isConnecting || isDisconnecting}
                className="flex-1 bg-violet-600 text-white py-2.5 px-4 rounded-md text-sm font-medium hover:bg-violet-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isConnecting ? (
                  <>
                    <LoaderCircle className="w-4 h-4 animate-spin mr-2" />
                    <span>Connecting...</span>
                  </>
                ) : (
                  'Connect Account'
                )}
              </button>

              <button
                onClick={handleDisconnect}
                disabled={isConnecting || isDisconnecting || !isConnected}
                className="flex-1 bg-white text-gray-700 py-2.5 px-4 rounded-md text-sm font-medium border border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isDisconnecting ? (
                  <>
                    <LoaderCircle className="w-4 h-4 animate-spin mr-2" />
                    <span>Disconnecting...</span>
                  </>
                ) : (
                  'Disconnect'
                )}
              </button>
            </div>

            <p className="text-xs text-gray-500 mt-4 text-center leading-relaxed">
              {isConnected
                ? 'Disconnect to revoke Kyva\'s access to your Google Calendar account.'
                : 'Connect your Google Calendar account to enable scheduling and event management features.'
              }
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default GoogleCalendarAuth;
